<script setup>
import { ref, computed } from 'vue'

const apartmentPrice = ref(4000000)
const downPayment = ref(1000000)
const creditTerm = ref(10)
const loanRate = ref(8.5)

const apartmentPriceText = computed(() => Number(apartmentPrice.value).toLocaleString('ru-RU'))
const downPaymentText = computed(() => Number(downPayment.value).toLocaleString('ru-RU'))

const creditTermMonth = computed(() => creditTerm.value * 12)
const loanRateMonth = computed(() => loanRate.value / 1200)
const amountCredit = computed(() => apartmentPrice.value - downPayment.value)
const monthlyPayment = computed(() =>
  creditTerm.value && loanRate.value
    ? Math.ceil(
        (loanRateMonth.value + loanRateMonth.value / (Math.pow(1 + loanRateMonth.value, creditTermMonth.value) - 1)) *
          amountCredit.value,
      )
    : 0,
)
const totalPayout = computed(() => Math.ceil(monthlyPayment.value * 12 * creditTerm.value))
const overpayment = computed(() => (creditTerm.value && loanRate.value ? totalPayout.value - amountCredit.value : 0))
const overpaymentPercent = computed(() =>
  amountCredit.value && loanRate.value ? Math.ceil(overpayment.value / (amountCredit.value / 100)) : 0,
)

const endPayments = computed(
  () => `${new Date().toLocaleString('default', { month: 'long' })} ${new Date().getFullYear() + creditTerm.value}`,
)

const procent = computed(() => (+apartmentPrice.value + overpayment.value) / 100)

const styleSum = computed(() => ({
  'background-color': 'rgb(46, 204, 113)',
  width: `${amountCredit.value / procent.value}%`,
}))
const styleOverpayment = computed(() => ({
  'background-color': 'rgb(255, 201, 46)',
  width: `${overpayment.value / procent.value}%`,
}))
const styleDownPayment = computed(() => ({
  'background-color': 'rgb(10,96,4)',
  width: `${+downPayment.value / procent.value}%`,
}))

function changeApartmentPrice(value) {
  apartmentPrice.value = Number(value.replace(/\D/g, ''))
}
function changeDownPayment(value) {
  downPayment.value = Number(value.replace(/\D/g, ''))
}
</script>

<template>
  <div class="mortgage-calk">
    <h1>Ипотечный калькулятор</h1>
    <p>
      Рассчитайте ипотеку калькулятором онлайн - узнайте платежи и график погашения кредита в банках. Чтобы выбрать
      выгодный вариант и рассчитать ипотечный кредит калькулятором, введите параметры и посмотрите переплаты в выбранных
      банках России, выберите наиболее подходящий кредит и оставьте заявку.
    </p>
    <div class="calk-body">
      <div class="inputs">
        <div class="input-box">
          <div class="input-range-wrap">
            <label for="input-sum">
              <span class="input-desc">Стоимость недвижимости, ₽</span>
              <input
                id="input-sum"
                class="input-text"
                data-sync="totalCost"
                :value="apartmentPriceText"
                type="text"
                @input="changeApartmentPrice($event.target.value)"
              />
              <div class="range-wrap-box">
                <input
                  type="range"
                  id="range-sum"
                  v-model="apartmentPrice"
                  class="input-range"
                  data-sync="totalCost"
                  min="1000000"
                  max="20000000"
                />
              </div>
            </label>
          </div>
        </div>

        <div class="input-box">
          <div class="input-range-wrap">
            <label for="input-sum">
              <span class="input-desc">Первоначальный взнос, ₽</span>
              <input
                type="text"
                id="input-sum"
                :value="downPaymentText"
                class="input-text"
                data-sync="totalCost"
                @input="changeDownPayment($event.target.value)"
              />
              <div class="range-wrap-box">
                <input
                  type="range"
                  id="range-sum"
                  v-model="downPayment"
                  class="input-range"
                  data-sync="totalCost"
                  min="0"
                  :max="apartmentPrice"
                />
              </div>
            </label>
          </div>
          <div v-if="+downPayment > +apartmentPrice" style="color: red">
            Первоначальный взнос должен быть меньше стоимости недвижимости
          </div>
        </div>

        <div class="amount-credit">
          Сумма кредита:
          <b>{{ amountCredit.toLocaleString('ru') }}</b>
          ₽
        </div>

        <div class="input-boxs">
          <div class="input-box input-boxs-left">
            <div class="input-range-wrap">
              <label for="input-sum">
                <span class="input-desc">Срок кредита, лет</span>
                <input
                  type="number"
                  id="input-sum"
                  v-model="creditTerm"
                  class="input-text"
                  data-sync="totalCost"
                  min="0"
                  max="30"
                />
              </label>
            </div>
          </div>
          <div class="input-box input-boxs-right">
            <div class="input-range-wrap">
              <label for="input-sum">
                <span class="input-desc">Ставка, %</span>
                <input
                  type="number"
                  id="input-sum"
                  v-model="loanRate"
                  class="input-text"
                  data-sync="totalCost"
                  min="0"
                  max="100"
                />
              </label>
            </div>
          </div>
        </div>

        <div class="mortgage-input-row mortgage-main-button-wrap">
          <a class="a-form new-orange-button" href="zakazat-zvonok" id="zvonok-form" data-fancybox-type="iframe">
            Получить консультацию
          </a>
        </div>
      </div>
      <div class="diagram">
        <div data-test="results-diagram">
          <div class="eQusqU">
            <div class="ijtHYs">
              <div class="Co_uP">
                <span class="ffqWK" style="background-color: rgb(10, 96, 4)"></span>
                <span class="X9IKv">Первоначальный взнос</span>
              </div>
            </div>
            <div class="ijtHYs">
              <div class="Co_uP">
                <span class="ffqWK" style="background-color: rgb(46, 204, 113)"></span>
                <span class="X9IKv">Кредит</span>
              </div>
            </div>
            <div class="ijtHYs">
              <div class="Co_uP">
                <span class="ffqWK" style="background-color: rgb(255, 201, 46)"></span>
                <span class="X9IKv">Переплата</span>
              </div>
            </div>
          </div>
          <div class="WHMSQ">
            <div direction="row" class="iWDKLr">
              <div class="VajEE" :style="styleDownPayment"></div>
              <div class="VajEE" :style="styleSum"></div>
              <div class="VajEE" :style="styleOverpayment"></div>
            </div>
          </div>
        </div>

        <div class="mortgage-items">
          <div class="mortgage-item full-credit-summ">
            <span class="text">Платёж в месяц</span>
            <span class="value" id="mortgage-credit-summ">{{ monthlyPayment.toLocaleString('ru') }}</span>
          </div>
          <div class="mortgage-item full-credit-summ">
            <span class="text">Сумма кредита</span>
            <span class="value" id="mortgage-credit-summ">{{ amountCredit.toLocaleString('ru') }}</span>
          </div>
          <div class="mortgage-item first-credit-summ">
            <span class="text">Первоначальный взнос</span>
            <span class="value" id="mortgage-first-summ">{{ downPayment.toLocaleString('ru') }}</span>
          </div>
          <div class="mortgage-item unlimit-credit-summ">
            <span class="text">Переплата по кредиту</span>
            <span class="value" id="mortgage-overpay">{{ overpayment.toLocaleString('ru') }}</span>
          </div>
          <div class="mortgage-item all-credit-summ">
            <span class="text">Общая сумма выплат</span>
            <span class="value" id="mortgage-alltotalsumm">{{ totalPayout.toLocaleString('ru') }}</span>
          </div>
          <div class="mortgage-item percent-credit-summ">
            <span class="text">Процент переплаты</span>
            <span class="value" id="mortgage-percentOverpay">{{ overpaymentPercent }}</span>
          </div>
          <div class="mortgage-item date-credit-summ">
            <span class="text">Окончание выплат</span>
            <span class="value" id="mortgage-finalDate">{{ endPayments }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.mortgage-calk {
  font-family: Inter, system-ui, Avenir, Helvetica, Arial, sans-serif;
  line-height: 1.5;
  font-weight: 400;

  font-synthesis: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-text-size-adjust: 100%;
  font-size: 13px;
  color: #282828;
  margin: 30px 50px;

  h1 {
    font-size: 35px;
    font-weight: 500;
  }

  p {
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0;
    margin-inline-end: 0;
    font-size: 14px;
  }

  .input-boxs {
    display: flex;
  }

  .input-boxs-left {
    width: 65%;
    margin-right: 10px;
  }
  .input-boxs-right {
    width: 35%;
  }

  .amount-credit {
    margin-bottom: 20px;
  }

  .calk-body {
    display: flex;
  }

  .inputs,
  .diagram {
    border-radius: 15px;
    background-color: #f0f3f5;
    padding: 15px;
  }

  .inputs {
    width: 33%;
    padding-top: 30px;
    margin-right: 10px;
  }

  .diagram {
    width: 77%;
    padding-top: 30px;
    margin-left: 10px;
  }

  .input-box {
    margin-bottom: 30px;
  }
  .input-range-wrap {
    position: relative;
  }
  .input-desc {
    position: absolute;
    top: -7px;
    font-size: 12px;
    left: 5px;
    background-color: #f0f3f5;
    padding: 0 6px;
  }
  .input-text {
    width: 100%;
    box-sizing: border-box;
    height: 40px;
    font-size: 14px;
    font-weight: 500;
    border: 1px solid #c1d6e4;
    background-color: #f0f3f5;
    padding-left: 10px;
    outline: none;
  }

  .range-wrap-box {
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 100%;
  }
  .input-range {
    position: relative;
    width: 100%;
    left: -1px;
    height: 4px;
    background: #c1d6e4;
    -webkit-appearance: none;
    outline: none;
    border-radius: 1px;
  }
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
  input[type='number'] {
    -moz-appearance: textfield;
  }

  .mortgage-items {
    margin-top: 30px;
  }

  .mortgage-items > .mortgage-item {
    display: flex;
    justify-content: space-between;
    border-top: 1px solid #ccc;
    padding: 10px 0;
    align-items: center;
  }
  .mortgage-month-summ > .text,
  .mortgage-items > .mortgage-item > .text {
    font-size: 14px;
    opacity: 0.7;
  }
  .mortgage-month-summ > .value,
  .mortgage-items > .mortgage-item > .value {
    font-size: 16px;
    font-weight: 700;
    display: inline;
  }
  .mortgage-month-summ > .value:after,
  .mortgage-items > .mortgage-item > .value:after {
    content: ' ₽';
    display: inline;
    font-weight: 400;
    opacity: 0.9;
    font-size: 14px;
  }
  .date-credit-summ > .value:after {
    content: ' г.' !important;
  }
  .percent-credit-summ > .value:after {
    content: ' %' !important;
  }

  .results-diagram {
    margin-bottom: 30px;
  }

  .eQusqU {
    display: flex;
    flex-flow: row wrap;
    margin-bottom: 10px;
  }
  .ijtHYs {
    flex: 0 1 auto;
    -webkit-box-flex: 0;
    width: auto;
  }
  .Co_uP {
    display: flex;
    align-items: baseline;
    margin-right: 16px;
  }
  .ffqWK {
    width: 8px;
    height: 8px;
    display: inline-block;
    border-radius: 50%;
    margin-right: 4px;
  }
  .j9Nmk .X9IKv {
    font-size: 12px;
    line-height: 16px;
  }
  .WHMSQ {
    width: 100%;
    border-radius: 4px;
    overflow: hidden;
    background: #eceff1;
    height: 8px;
  }
  .iWDKLr {
    display: flex;
    flex-direction: row;
  }
  .VajEE {
    transition: width 1s;
    height: 8px;
  }

  .mortgage-main-button-wrap {
    display: flex;
    justify-content: center;
  }
  .new-orange-button:hover,
  .new-orange-button {
    transition: all 0.3s ease-in-out;
  }
  .new-orange-button {
    display: flex;
    color: #fff;
    background-image: linear-gradient(90deg, #f6bf5b, #eb9033);
    padding: 10px 30px;
    text-align: center;
    font-weight: 700;
    flex-wrap: wrap;
    min-width: 160px;
    justify-content: center;
    align-items: center;
    text-shadow: 1px 1px 1px rgba(150, 150, 150, 0.67);
    box-sizing: border-box;
    border-radius: 5px;
  }
  a,
  a:hover,
  a:focus {
    text-decoration: none;
    transition: all 0.35s ease-in-out;
  }
}

@media (max-width: 940px) {
  .mortgage-calk {
    .calk-body {
      display: block;
    }

    .inputs,
    .diagram {
      width: 100%;
      margin: 0;
    }
    .diagram {
      margin-top: 30px;
    }
  }
}
</style>
